type NormalizingResult<T> =
  | { valid: true; normalized: T; message?: never }
  | { valid: false; message: string; normalized?: never };

type SimpleResult =
  | { valid: true; message?: never }
  | { valid: false; message: string; normalized?: never };

const PHONE_INVALID = 'Please enter a valid UK phone number.';
const EMAIL_INVALID = 'Please enter a valid email address.';
const DOB_INVALID = 'Please enter a valid date of birth (DD-MM-YYYY).';

const EMAIL_REGEX =
  /^(?=.{1,254}$)(?=.{1,64}@)[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

const toDigits = (value: string) => value.replace(/\D/g, '');

const normalizeUkPhoneInternal = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const digits = toDigits(raw);
  if (!digits) return '';

  let national = '';
  if (raw.startsWith('+')) {
    if (!digits.startsWith('44')) return '';
    national = digits.slice(2);
  } else if (digits.startsWith('0044')) {
    national = digits.slice(4);
  } else if (digits.startsWith('44') && raw.startsWith('44')) {
    national = digits.slice(2);
  } else if (digits.startsWith('0')) {
    national = digits.slice(1);
  } else {
    return '';
  }

  if (!national) return '';
  if (national.startsWith('0') && national.length === 11) {
    national = national.slice(1);
  }
  return `+44${national}`;
};

export function validateUkPhone(value: string): NormalizingResult<string> {
  const normalized = normalizeUkPhoneInternal(value);
  if (!normalized) return { valid: false, message: PHONE_INVALID };
  const digits = toDigits(normalized);
  if (!digits.startsWith('44')) return { valid: false, message: PHONE_INVALID };
  const national = digits.slice(2);
  if (national.length !== 10) return { valid: false, message: PHONE_INVALID };
  if (national.startsWith('0')) return { valid: false, message: PHONE_INVALID };
  return { valid: true, normalized };
}

export function validateEmail(value: string): SimpleResult {
  if (!EMAIL_REGEX.test(value.trim())) {
    return { valid: false, message: EMAIL_INVALID };
  }
  return { valid: true };
}

export function validateDob(value: string): NormalizingResult<string> {
  if (!value) return { valid: false, message: DOB_INVALID };
  const match = value.match(/^(\d{2})([-/])(\d{2})\2(\d{4})$/);
  if (!match) return { valid: false, message: DOB_INVALID };
  const day = Number(match[1]);
  const month = Number(match[3]);
  const year = Number(match[4]);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    return { valid: false, message: DOB_INVALID };
  }
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return { valid: false, message: DOB_INVALID };
  }
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return { valid: false, message: DOB_INVALID };
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { valid: false, message: DOB_INVALID };
  }
  const iso = `${match[4]}-${match[3]}-${match[1]}`;
  return { valid: true, normalized: iso };
}
